// Path: sec-parser/client/src/components/benchmark/PromptInspectorModal.jsx
// Shows exactly what text + prompt gets sent to the AI models before running benchmark
import React, { useState, useEffect } from "react";
import {
  Modal, Tabs, Typography, Tag, Space, Spin, Alert,
  Statistic, Row, Col, Badge, Button, Tooltip,
} from "antd";
import {
  FileTextOutlined, RobotOutlined, MessageOutlined,
  CopyOutlined, InfoCircleOutlined, EyeOutlined,
} from "@ant-design/icons";
import { getPromptPreview } from "../../services/api";

const { Text, Paragraph } = Typography;

const CODE_STYLE = {
  background: "#0d1117",
  color: "#c9d1d9",
  padding: "16px",
  borderRadius: "6px",
  fontSize: "12px",
  fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  overflowY: "auto",
  maxHeight: "420px",
  lineHeight: "1.6",
  border: "1px solid #30363d",
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).catch(() => {});
};

export default function PromptInspectorModal({ filingId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data,    setData]    = useState(null);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!open || !filingId) return;
    setLoading(true);
    setData(null);
    setError(null);
    getPromptPreview(filingId)
      .then(({ data }) => setData(data))
      .catch((err)    => setError(err.response?.data?.error || err.message))
      .finally(()     => setLoading(false));
  }, [open, filingId]);

  const fmt = (n) => n?.toLocaleString() ?? "—";

  const tabItems = data ? [
    {
      key:   "extracted",
      label: (
        <span>
          <FileTextOutlined /> Extracted Text
          <Badge
            count={`${fmt(data.filingTextChars)} chars`}
            style={{ background: "#1677ff", marginLeft: 6, fontSize: 10 }}
          />
        </span>
      ),
      children: (
        <div>
          <Space style={{ marginBottom: 12 }} wrap>
            <Tag color="blue">Source: {data.source}</Tag>
            <Tag color="green">Raw: {fmt(data.rawTextChars)} chars</Tag>
            <Tag color="orange">Sent to AI: {fmt(data.filingTextChars)} chars</Tag>
            {data.rawTextChars > data.filingTextChars && (
              <Tag color="red">
                Truncated: {fmt(data.rawTextChars - data.filingTextChars)} chars dropped
              </Tag>
            )}
          </Space>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={
              <span>
                This is the <strong>extracted &amp; structured text</strong> sent to every AI model.
                {data.source === "edgar-url"
                  ? " Top section = XBRL structured facts. Bottom section = MD&A narrative text."
                  : " Text was extracted from the uploaded file and financial sections were located."}
              </span>
            }
          />
          <div style={{ position: "relative" }}>
            <Tooltip title="Copy extracted text">
              <Button
                size="small"
                icon={<CopyOutlined />}
                style={{ position: "absolute", top: 8, right: 8, zIndex: 10, background: "#21262d", borderColor: "#30363d", color: "#c9d1d9" }}
                onClick={() => copyToClipboard(data.sections.extractedText)}
              />
            </Tooltip>
            <div style={CODE_STYLE}>{data.sections.extractedText}</div>
          </div>
        </div>
      ),
    },
    {
      key:   "system",
      label: <span><RobotOutlined /> System Prompt</span>,
      children: (
        <div>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="This system prompt is identical for all models — ensuring a fair benchmark. It defines the financial analyst role, instructions, and the JSON template structure."
          />
          <div style={{ position: "relative" }}>
            <Tooltip title="Copy system prompt">
              <Button
                size="small"
                icon={<CopyOutlined />}
                style={{ position: "absolute", top: 8, right: 8, zIndex: 10, background: "#21262d", borderColor: "#30363d", color: "#c9d1d9" }}
                onClick={() => copyToClipboard(data.sections.systemPrompt)}
              />
            </Tooltip>
            <div style={CODE_STYLE}>{data.sections.systemPrompt}</div>
          </div>
        </div>
      ),
    },
    {
      key:   "user",
      label: <span><MessageOutlined /> User Message</span>,
      children: (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="This is the user message — a brief instruction followed by the full extracted text. Some models receive it truncated if the text exceeds their context limit."
          />
          <div style={{ position: "relative" }}>
            <Tooltip title="Copy user message">
              <Button
                size="small"
                icon={<CopyOutlined />}
                style={{ position: "absolute", top: 8, right: 8, zIndex: 10, background: "#21262d", borderColor: "#30363d", color: "#c9d1d9" }}
                onClick={() => copyToClipboard(data.sections.userMessage)}
              />
            </Tooltip>
            <div style={CODE_STYLE}>{data.sections.userMessage}</div>
          </div>
        </div>
      ),
    },
  ] : [];

  return (
    <Modal
      title={
        <Space>
          <EyeOutlined style={{ color: "#1677ff" }} />
          <span>Prompt Inspector</span>
          {data?.companyName && <Tag color="blue">{data.companyName}</Tag>}
          {data?.filingType  && <Tag>{data.filingType}</Tag>}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      styles={{ body: { padding: "16px 24px" } }}
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, color: "#888" }}>Loading prompt data…</div>
        </div>
      )}

      {error && <Alert type="error" showIcon message={error} />}

      {data && !loading && (
        <>
          {/* Stats row */}
          <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Statistic title="Raw Extracted" value={fmt(data.rawTextChars)} suffix="chars" valueStyle={{ fontSize: 16 }} />
            </Col>
            <Col span={6}>
              <Statistic
                title={data.chunkCount > 1 ? `Per Chunk (of ${data.chunkCount})` : "Sent to AI"}
                value={fmt(data.chunkCount > 1 ? data.chunkSize : data.filingTextChars)}
                suffix="chars"
                valueStyle={{ fontSize: 16, color: data.chunkCount > 1 ? "#fa8c16" : "#1677ff" }}
              />
            </Col>
            <Col span={6}>
              <Statistic title="System Prompt" value={fmt(data.sections.systemPrompt.length)} suffix="chars" valueStyle={{ fontSize: 16 }} />
            </Col>
            <Col span={6}>
              <Statistic
                title={data.chunkCount > 1 ? "Chunks" : "Full Coverage"}
                value={data.chunkCount > 1 ? data.chunkCount : "1 pass"}
                valueStyle={{ fontSize: 16, color: data.chunkCount > 1 ? "#fa8c16" : "#52c41a" }}
              />
            </Col>
          </Row>
          {data.chunkCount > 1 && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
              message={`Multi-pass mode: ${data.chunkCount} chunks × ~${fmt(data.chunkSize)} chars. Results from each chunk are merged. Preview below shows chunk 1.`}
            />
          )}
          {data.urlModeAvailable && (
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message={`URL mode available — GPT-4o and Gemini can read this filing directly from: ${data.sourceUrl?.slice(0, 80)}…`}
            />
          )}

          <Tabs defaultActiveKey="extracted" items={tabItems} />
        </>
      )}
    </Modal>
  );
}